import { ControlOptions } from "leaflet";
import {
  Alert,
  alpha,
  Fab,
  Grow,
  IconButton,
  Paper,
  Portal,
  styled,
  Switch,
  Toolbar,
  Tooltip,
  Typography,
  Zoom,
} from "@mui/material";
import { AddLocation, ArrowLeft, ArrowRight, Close } from "@mui/icons-material";

import { useTranslation } from "react-i18next";
import { useMapPoint } from "../../hooks/mapHooks";
import { useConfig } from "../../hooks/settingHooks";

import { LayerProperties } from "../../types";

import { MapControlBase } from "./MapControlBase";
import { ActiveFab } from "../button/ActiveFab";
import { MapClickHandler } from "./MapClickHandler";
import { InlineDrawer } from "../drawers/InlineDrawer";
import { LayerPointInfosForm } from "../form/point/LayerPointInfoForm";
import {
  MapPanelEvent,
  MapPanelState,
  MapPanelStatePoint,
} from "../../machines/MapPanelMachine";
import { useSearchParams } from "react-router-dom";
import { ChangeEvent } from "react";
import { PointInfoLinks } from "components/form/point/PointInfoLinks";

interface SearchControlProps extends ControlOptions {
  overlayContainer: Element;
  drawerContainer: Element;
  layerProperties: LayerProperties[];

  stateValue: MapPanelState;
  updateState: (value: MapPanelEvent["type"]) => void;
}

export function LayerPointControl(props: SearchControlProps) {
  const {
    stateValue,
    updateState,
    layerProperties,
    overlayContainer,
    drawerContainer,
    ...controlProps
  } = props;
  const [point, setPoint] = useMapPoint();
  const { t } = useTranslation();
  const config = useConfig();
  const [params, setParams] = useSearchParams();

  function isMapStatePoint(data: MapPanelState): data is MapPanelStatePoint {
    return data.hasOwnProperty("point");
  }

  return (
    <MapControlBase {...controlProps}>
      <Tooltip title={<>{t("label.pointInformation")}</>} placement="left">
        <>
          <ActiveFab
            className={isMapStatePoint(stateValue) ? "active" : "inactive"}
            size="small"
            variant="circular"
            onClick={() =>
              updateState(
                isMapStatePoint(stateValue) ? "CLOSING" : "MODE_POINT"
              )
            }
          >
            <AddLocation />
          </ActiveFab>
          <Zoom in={point !== null}>
            <PositionedFab
              variant="tiny"
              className="remove-point"
              onClick={() => setPoint(null)}
            >
              <Close />
            </PositionedFab>
          </Zoom>
        </>
      </Tooltip>

      {isMapStatePoint(stateValue) && <MapClickHandler />}
      <Portal container={overlayContainer}>
        <Grow
          in={isMapStatePoint(stateValue) && !point}
          style={{ transformOrigin: "50% 0 0" }}
        >
          <Alert variant="standard" icon={false} color={undefined}>
            {t("message.clickOnMapForPointInformationOfLayers")}
          </Alert>
        </Grow>
      </Portal>
      <Portal container={drawerContainer}>
        {isMapStatePoint(stateValue) && stateValue.point === "pointCollapsed" && (
          <StyledFloatingIconButton
            className="has-point-open-button"
            size="small"
            onClick={() => updateState("OPENING_POINT")}
          >
            <ArrowLeft />
          </StyledFloatingIconButton>
        )}
        <StyledInlineDrawer
          open={isMapStatePoint(stateValue) && stateValue.point === "pointOpen"}
          variant="persistent"
          anchor="right"
        >
          <Paper elevation={3}>
            <Toolbar className="panel-title">
              <Typography variant="subtitle2">
                {t("label.pointInformation")}
              </Typography>
              <Switch
                size="small"
                checked={params.get("pl") === "all"}
                onChange={(_: ChangeEvent, checked: boolean) => {
                  const clone = new URLSearchParams(params);
                  if (checked) clone.set("pl", "all");
                  else clone.delete("pl");
                  setParams(clone);
                }}
              />
              <small className="subtitle-info">
                {params.get("pl") === "all"
                  ? t("label.allLayers")
                  : t("label.visibleLayers")}
              </small>
              <IconButton
                edge="end"
                size="small"
                onClick={() => updateState("CLOSING_POINT")}
              >
                <ArrowRight />
              </IconButton>
            </Toolbar>
          </Paper>
          {config && (
            <LayerPointInfosForm
              className={
                point &&
                isMapStatePoint(stateValue) &&
                stateValue.point === "pointCollapsed"
                  ? "content-hidden"
                  : ""
              }
              config={config}
              layerProperties={layerProperties}
            />
          )}
          <PointInfoLinks />
        </StyledInlineDrawer>
      </Portal>
    </MapControlBase>
  );
}
const PositionedFab = styled(Fab)`
  position: absolute;
  left: ${({ theme }) => theme.spacing(-0.5)};
  bottom: ${({ theme }) => theme.spacing(-0.5)};
`;

const StyledFloatingIconButton = styled(IconButton)`
  position: absolute;
  top: 20px;
  left: -10px;
  z-index: 2;
  justify-self: flex-start;
  width: 20px;
  height: 20px;
  color: #000;
  background-color: #fff;

  &:hover {
    background-color: #ccc;
  }
`;

const StyledInlineDrawer = styled(InlineDrawer)`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;

  .panel-title {
    position: relative;

    .MuiSwitch-root {
      margin-left: ${({ theme }) => theme.spacing(1)};
    }

    .subtitle-info {
      color: ${({ theme }) =>
        alpha(theme.typography.subtitle2.color?.toString() || "#fff", 0.4)};
      position: absolute;
      bottom: 0;
    }
  }

  &.is-closed > .MuiPaper-root {
    overflow-x: hidden;
  }

  &.has-point {
    & > .MuiPaper-root {
      transform: translateX(0px) !important;
      visibility: unset !important;
      padding: 0;
    }
  }

  .content-hidden {
    & > * {
      visibility: hidden;
    }
  }

  .MuiPaper-elevation {
    background-image: unset;
    z-index: 1;
  }

  .MuiToolbar-root {
    display: flex;

    .MuiIconButton-root {
      margin-left: auto;
    }
  }

  .grow {
    flex: 1 1 100%;
    z-index: 0;
  }
`;
